import React, { useState } from 'react';
import { Button, Input } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { PayrollProviderModal } from '../PayrollProviderModal';
import { idHash } from '../../utils/idHash';
import { getProvider } from '../../utils/getApiKey';
import { fetchAuthenticated, updateAccount } from '../../api/api';
import { extractProviderInfo } from '../../utils/extractProviderInfo';
import styles from './Login.module.scss';

function Login() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [provider, setProvider] = useState('pinwheel');
    const [linkToken, setLinkToken] = useState('');
    const [publicToken, setPublicToken] = useState(null);
    const [hasPinwheel, setHasPinwheel] = useState(false);

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!email?.trim()) {
            setError('Please enter your email');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const id = idHash([email, getProvider()]);
            const response = await fetchAuthenticated(id);
            const data = response?.data;

            if (response.status !== 200 && response.status !== 202 && response.status !== 304) {
                throw new Error(data?.message || 'Failed to authenticate');
            }

            const providerInfo = extractProviderInfo(data);
            const token = providerInfo?.linkToken || data?.linkToken || data?.pinwheel?.token || data?.atomic?.token || data?.atomic?.publicToken;

            if (token) {
                setProvider(providerInfo?.provider || data?.provider || 'pinwheel');
                setLinkToken(token);
                setPublicToken(providerInfo?.publicToken || null);
                setHasPinwheel(!!(data?.account_summary?.accounts?.length));
                try {
                    await updateAccount(id, { account_info: { email } });
                } catch (updateErr) {
                    console.error('Update account error:', updateErr);
                }
                setIsModalOpen(true);
            } else if (data?.authenticated || data?.account_summary) {
                setError(null);
            } else {
                setError('No link token received. User may have completed onboarding.');
            }
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const handlePayrollSuccess = (accountId, platformId, reauth) => {
        console.log('Payroll link success:', { accountId, platformId, reauth });
        setIsModalOpen(false);
        setError(null);
    };

    const getApiKeyHint = () => {
        const provider = getProvider();
        const hint = provider === 'atomic' ? 'Using Atomic' : 'Using Pinwheel';
        return `${hint}. Add ?provider=atomic or ?provider=pinwheel to URL to switch.`;
    };

    return (
        <div className={styles.loginContainer}>
            <div className={styles.loginCard}>
                <h1 className={styles.title}>Payroll Provider Test</h1>
                <p className={styles.subtitle}>Enter your email to test payroll linking</p>

                <form onSubmit={handleSubmit}>
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Email</label>
                        <Input
                            size="large"
                            prefix={<MailOutlined />}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onPressEnter={handleSubmit}
                            placeholder="email@example.com"
                            status={error ? 'error' : ''}
                            className={styles.input}
                            autoComplete="email"
                            disabled={loading}
                        />
                    </div>

                    {error && <p className={styles.errorText}>{error}</p>}

                    <Button
                        type="primary"
                        size="large"
                        loading={loading}
                        onClick={handleSubmit}
                        className={styles.submitButton}
                        block
                        style={{ marginTop: 24 }}
                    >
                        Continue
                    </Button>
                </form>

                <div className={styles.providerHint}>{getApiKeyHint()}</div>
            </div>

            <PayrollProviderModal
                provider={provider}
                operation="verify"
                isOpen={isModalOpen}
                linkToken={linkToken}
                publicToken={publicToken}
                hasPinwheel={hasPinwheel}
                setIsOpen={setIsModalOpen}
                onSuccess={handlePayrollSuccess}
                reauth={false}
                onError={(err) => {
                    console.error('Payroll modal error:', err);
                    setError(err?.message || 'Payroll linking failed');
                }}
            />
        </div>
    );
}

export default Login;
